import React, { useEffect, useState } from "react";
import { X, Loader2, Send, PenLine } from "lucide-react";
import { toast } from "sonner";
import SendForm from "./SendForm";
import YousignPanel, { signerReady } from "./YousignPanel";
import { api as http } from "../lib/api";
import { formReady, loadDraft, sendForm } from "../lib/docSend";
import { API } from "../lib/api";

export function Modal({ title, onClose, children, footer, testid, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6">
      <div className={`bg-white rounded-xl shadow-2xl w-full ${wide ? "max-w-5xl" : "max-w-2xl"} max-h-[92vh] flex flex-col border border-norea-border`} data-testid={testid}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-norea-border">
          <h3 className="font-heading text-lg font-medium">{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-norea-surfaceHover" data-testid={`${testid}-close`}><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        <div className="px-5 py-3 border-t border-norea-border flex justify-end gap-2">{footer}</div>
      </div>
    </div>
  );
}

function InlinePreview({ dossierId, doc }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true, obj = null;
    setUrl(null); setErr(false);
    fetch(`${API}/dossiers/${dossierId}/send-docs/${doc.id}/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem("norea_token")}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => { obj = URL.createObjectURL(b); if (alive) setUrl(obj); })
      .catch(() => alive && setErr(true));
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [dossierId, doc.id]);
  if (err) return <div className="h-full flex items-center justify-center text-xs text-red-700" data-testid="send-inline-preview-error">Aperçu indisponible</div>;
  if (!url) return <div className="h-full flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  return <iframe src={url} title={doc.name} className="w-full h-full border-0 rounded" data-testid="send-inline-preview" />;
}

export default function SendDocModal({ dossierId, docIds, title, onClose, onSent }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("send");
  const [signer, setSigner] = useState(null);
  const role = (r, tid) => loadDraft(dossierId, docIds, r, tid).then((f) => {
    setForm(f);
    if (r === undefined && f.signable) { setSigner(f.signer); setMode(f.yousign.mode === "yousign" ? "sign" : "send"); }
  }).catch(() => toast.error("Chargement impossible"));
  const sign = async () => {
    setBusy(true);
    try {
      await http.post(`/dossiers/${dossierId}/signatures`, { doc_id: docIds[0], ...signer });
      toast.success("Demande de signature envoyée par Yousign");
      onSent?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi Yousign impossible"); }
    finally { setBusy(false); }
  };
  useEffect(() => { role(undefined); }, [dossierId, docIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    const win = form.channels.whatsapp ? window.open("", "_blank") : null;
    setBusy(true);
    try {
      await sendForm(dossierId, form, win);
      toast.success(`Envoyé${form.channels.email ? " par email" : ""}${form.channels.email && form.channels.whatsapp ? " et" : ""}${form.channels.whatsapp ? " — WhatsApp ouvert" : ""}`);
      onSent?.();
      onClose();
    } catch (e) { win?.close(); toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={title || form?.docs.map((d) => d.name).join(" + ") || "Envoyer"} onClose={onClose} testid="send-doc-modal" wide
      footer={<>
        <button onClick={onClose} className="btn-secondary text-sm">Annuler</button>
        {mode === "sign"
          ? <button onClick={sign} disabled={busy || !signerReady(signer) || !form?.yousign.configured} className="btn-primary text-sm" data-testid="yousign-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Faire signer (Yousign)</button>
          : <button onClick={send} disabled={busy || !formReady(form)} className="btn-primary text-sm" data-testid="send-doc-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer</button>}
      </>}>
      {!form ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="h-[60vh] rounded border border-norea-border bg-norea-bg/40 p-1"><InlinePreview dossierId={dossierId} doc={form.docs[0]} /></div>
          <div className="space-y-4">
            {form.signable && (
              <div className="inline-flex p-0.5 rounded-md border border-norea-border bg-white" data-testid="send-mode-switch">
                {[["send", "Envoyer le PDF"], ["sign", "Faire signer (Yousign)"]].map(([k, l]) => (
                  <button key={k} onClick={() => setMode(k)} className={`px-3 py-1.5 text-xs rounded transition-colors ${mode === k ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`} data-testid={`send-mode-${k}`}>{l}</button>
                ))}
              </div>
            )}
            {mode === "sign" && signer ? <YousignPanel signer={signer} setSigner={setSigner} yousign={form.yousign} /> : <SendForm dossierId={dossierId} form={form} setForm={setForm} onRole={(r) => role(r)} onTemplate={(tid) => role(form.role, tid)} />}
          </div>
        </div>
      )}
    </Modal>
  );
}
